import { offlineDb } from '@/db/offlineDb';
import { supabase } from '@/integrations/supabase/client';
import OfflineLogger from '@/utils/offlineLogger';

interface PrimaryListConflictParams {
  cleanData: any;
  entityId: string;
  tableName: string;
  loggerContext: string;
  fallbackUserId?: string | null;
}

interface PrimaryListConflictResult {
  /** true se la lista in arrivo è stata degradata a non-primaria */
  demoted: boolean;
  /** id della lista primaria già esistente sul server, se trovata */
  existingPrimaryId?: string;
}

/**
 * Risolve il conflitto sull'indice univoco parziale
 * `unique_primary_list_per_user_idx` (una sola lista `is_primary=true` per
 * utente).
 *
 * Scenario: una lista primaria creata offline (UUID locale) viene sincronizzata
 * mentre lato server ne esiste già una primaria con id diverso (creata in
 * onboarding o da un altro device). L'upsert `onConflict: 'id'` non rileva il
 * conflitto (gli id non collidono), ma il vincolo parziale sì → errore 23505.
 *
 * Strategia conservativa (no data loss): la lista in arrivo viene **degradata**
 * a non-primaria. La primaria già presente sul server resta tale; la lista
 * locale diventa una lista secondaria mantenendo i propri item.
 *
 * Muta `cleanData.is_primary` a `false` e allinea il record locale Dexie quando
 * rileva una primaria preesistente con id diverso.
 */
export async function resolvePrimaryListConflict({
  cleanData,
  entityId,
  tableName,
  loggerContext,
  fallbackUserId
}: PrimaryListConflictParams): Promise<PrimaryListConflictResult> {
  // Interessa solo le liste che si presentano come primarie.
  if (cleanData?.is_primary !== true) {
    return { demoted: false };
  }

  const userId = cleanData?.user_id || fallbackUserId;
  if (!userId) {
    OfflineLogger.warn(
      `${loggerContext}.resolvePrimaryListConflict`,
      'Impossibile verificare la lista primaria: user_id mancante'
    );
    return { demoted: false };
  }

  try {
    const { data: existingPrimary, error } = await supabase
      .from(tableName)
      .select('id')
      .eq('user_id', userId)
      .eq('is_primary', true)
      .order('created_at', { ascending: true })
      .limit(1)
      .maybeSingle();

    if (error) {
      OfflineLogger.error(
        `${loggerContext}.resolvePrimaryListConflict`,
        `Errore nella ricerca della lista primaria esistente: ${error.message}`
      );
      return { demoted: false };
    }

    // Nessuna primaria sul server, oppure la primaria è proprio questa lista
    // (doppio sync dello stesso id): nessun conflitto da risolvere.
    if (!existingPrimary || existingPrimary.id === entityId) {
      return { demoted: false };
    }

    return demoteIncomingList({
      cleanData,
      entityId,
      existingPrimaryId: existingPrimary.id as string,
      loggerContext
    });
  } catch (unexpectedError) {
    OfflineLogger.error(
      `${loggerContext}.resolvePrimaryListConflict`,
      unexpectedError
    );
    return { demoted: false };
  }
}

async function demoteIncomingList({
  cleanData,
  entityId,
  existingPrimaryId,
  loggerContext
}: {
  cleanData: any;
  entityId: string;
  existingPrimaryId: string;
  loggerContext: string;
}): Promise<PrimaryListConflictResult> {
  cleanData.is_primary = false;

  try {
    await offlineDb.shoppingLists.update(entityId, {
      is_primary: false,
      _lastModified: Date.now()
    });
  } catch (updateError) {
    OfflineLogger.warn(
      `${loggerContext}.resolvePrimaryListConflict`,
      `Impossibile degradare la lista locale ${entityId}: ${updateError}`
    );
  }

  OfflineLogger.info(
    `${loggerContext}.resolvePrimaryListConflict`,
    `Lista ${entityId} degradata a non-primaria: esiste già la primaria ${existingPrimaryId} per l'utente`
  );

  return { demoted: true, existingPrimaryId };
}
